<script setup lang="ts">
import IOHeroContentMenuBuffer from '~/components/io/io-hero/content/IOHeroContentMenuBuffer.vue'
import Button from '~/components/Button.vue'
import { useIOStore } from '@/stores/io'
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/vue-splide/css'
import IOHeroOverlays from '~/components/io/io-hero/layers/IOHeroOverlays.vue'

const props = withDefaults(defineProps<{
  id: string,
  content?: any,
}>(), {
  id: 'index',
})

const router = useRouter()
const $io = useIOStore()

// Splide carousel ref
const splideRef = ref<any>(null)

// Track active slide for Ken Burns effect
const activeSlideIndex = ref(0)

// Categories for the carousel
const categories = ref([
  {
    id: 'kitchens',
    name: 'Kitchens',
    title: 'Professional Kitchen Hygiene',
    description: 'World-class cleaning solutions for commercial kitchens. Keep your food preparation areas spotless and compliant with the highest hygiene standards.',
    image: '/images/kitchen.jpg'
  },
  {
    id: 'disinfection',
    name: 'Disinfection',
    title: 'Advanced Disinfection Solutions',
    description: 'Hospital-grade disinfectants and sanitizers to protect your guests and staff from harmful pathogens.',
    image: '/images/handwash.jpg'
  },
  {
    id: 'laundry',
    name: 'Laundry',
    title: 'Industrial Laundry Excellence',
    description: 'Professional laundry chemicals that deliver pristine results while protecting fabrics and reducing environmental impact.',
    image: '/images/laundry.jpg'
  },
  {
    id: 'rooms',
    name: 'Rooms',
    title: 'Guest Room Perfection',
    description: 'Complete room care solutions that ensure every guest experiences the ultimate in cleanliness and comfort.',
    image: '/images/housekeeping.jpg'
  },
  {
    id: 'general',
    name: 'General Cleaning',
    title: 'Comprehensive Cleaning Solutions',
    description: 'Multi-purpose cleaning products for all areas of your facility, from lobbies to back-of-house operations.',
    image: '/images/cleaning.jpg'
  }
])

const currentIndex = ref(0)
const currentImage = ref(categories.value[0].image)

// Navigation functions
const goToCategory = (index: number) => {
  currentIndex.value = index
  currentImage.value = categories.value[index].image
  
  // Update the IOHero media background
  updateHeroBackground(categories.value[index].image)
}

const nextCategory = () => {
  const next = (currentIndex.value + 1) % categories.value.length
  goToCategory(next)
}

const prevCategory = () => {
  const prev = currentIndex.value === 0 ? categories.value.length - 1 : currentIndex.value - 1
  goToCategory(prev)
}

// Function to update hero background through the IO store
const updateHeroBackground = (imageSrc: string) => {
  const newMedia = {
    type: 'image',
    transition: { mode: 'out-in' },
    media: {
      src: imageSrc,
      alt: categories.value[currentIndex.value].title
    }
  }
  // Also sync with Splide carousel
  if (splideRef.value) {
    splideRef.value.go(currentIndex.value)
  }
}

// Handle Splide slide change
const onSlideChange = (splide: any) => {
  currentIndex.value = splide.index
  currentImage.value = categories.value[splide.index].image
  activeSlideIndex.value = splide.index
}

// Carousel options
const splideOptions = {
  type: 'fade',
  rewind: true,
  autoplay: true,
  interval: 5000,
  pauseOnHover: false,
  arrows: false,
  pagination: false,
  speed: 1000,
  dragMinThreshold: {
    mouse: 0,
    touch: 10
  }
}
const overlays = {
      transition: { mode: 'out-in' },
      overlays: [{
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 180, // top -> bottom
            stops: [
              { color: 'rgba(0,0,0,0.00)', at: '45%' },
              { color: 'rgba(0,0,0,0.44)', at: '80%' },
              { color: 'rgba(0,0,0,0.66)',  at: '100%' }
            ],
            blendMode: 'normal'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                stops: [
                  { color: 'rgba(0,0,0,0.00)', at: '40%' },
                  { color: 'rgba(0,0,0,0.20)', at: '78%' },
                  { color: 'rgba(0,0,0,0.60)',  at: '100%' }
                ]
              }
            }
          ]
        },
        {
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 'to right',
            stops: [
              { color: 'rgba(0,40,140,0.45)', at: '0%'  },
              { color: 'rgba(0,40,140,0.20)', at: '35%' },
              { color: 'rgba(0,40,140,0.00)', at: '70%' }
            ],
            blendMode: 'plus-lighter'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                direction: '80deg',
                stops: [
                  { color: 'rgba(24,81,135,0.8)', at: '35%' },
                  { color: 'rgba(0,40,140,0)', at: '60%' }
                ],
                blendMode: 'plus-lighter'
              }
            }
          ]
        }
      ]
    }


// Auto-rotate categories
let autoRotateInterval: any = null

onMounted(() => {
  // Initialize with first image
  updateHeroBackground(categories.value[0].image)
})

</script>

<template>
  <div :class="['io-hero-content hero-home h-full', id]">

    <div class="layer bg-image-carousel w-100%! ds:w-100%!" style="position: absolute; top: 0; left: auto; right: 0; width: 100%; height: 100%;">
      <Splide 
        ref="splideRef"
        :options="splideOptions" 
        @splide:moved="onSlideChange"
        class="splide-background"
      >
        <SplideSlide 
          v-for="(category, index) in categories" 
          :key="category.id"
          class="slide-item"
        >
          <div 
            :class="['slide-bg-image', { 'ken-burns-active': index === activeSlideIndex }]"
            :style="{ backgroundImage: `url(${category.image})` }"
          ></div>
        </SplideSlide>
      </Splide>
    </div>

    <Frame class="layer overlay absolute inset-0 z-1 pointer-events-none">
      <IOHeroOverlays :overlays="overlays" class="layer overlay" />
    </Frame>

    <Frame class="layer content flex flex-col items-center justify-center gpt-8 gpb-4 ds:gpb-4" style="position: relative; z-index: 2;" w="max-w-92% mx-auto">

       <!-- Content Overlay -->
      <!-- <IOHeroContentMenuBuffer :id="id" /> -->

      <!-- Global Carousel Controls -->
      <div v-if="false" class="carousel-controls">
        <button 
          @click="prevCategory"
          class="carousel-btn carousel-btn-prev"
          aria-label="Previous slide"
        >
          <i class="i-mdi:chevron-left" />
        </button>
        <button 
          @click="nextCategory"
          class="carousel-btn carousel-btn-next"
          aria-label="Next slide"
        >
          <i class="i-mdi:chevron-right" />
        </button>
      </div>
      
      <Frame class="hero-content-wrapper" w="relative z-10 h-full flex items-center">
        <Frame class="content-container" w="w-full max-w-7xl mx-auto px-4">
          <Frame class="hero-content" w="grid grid-cols-[1fr] ds:grid-cols-[2fr_1fr] dm:grid-cols-[1fr_1fr] gap-8 items-center">
            
            <!-- Left Content -->
            <Frame class="content-left" w="space-y-6">
              <div class="sec-hairline">
                Welcome to Altracare
              </div>
              
              <h1 class="hero-title font-secondary">
                Zanzibar's most Trusted<br/>
                Partner in Hygiene &<br/>
                Hospitality Care
              </h1>
              
              <!-- <p class="hero-description">
                Altracare is a supplier of world class eco-friendly cleaning solutions for all
                industries, primarily for the hospitality industry keep your laundry, kitchen,
                and rooms spotless.
              </p> -->
              
              <!-- <Frame class="hero-actions" w="flex gap-4">
                <Button 
                  btn="light-blue" 
                  class="hero-btn-primary"
                  @click="router.push('/products')" 
                >
                  Products
                </Button>
                <Button 
                  btn="light-gray" 
                  class="hero-btn-outline"
                  @click="router.push('/services')"
                >
                  Services
                </Button>
              </Frame> -->
            </Frame>
            
            <!-- Right Content - Category Selector -->
            <Frame class="content-right hidden ds:flex" w="flex items-end justify-center ds:justify-end">
              <Frame v-if="false" class="category-selector" w="bg-white/10 backdrop-blur-md rounded-lg p-4">
                <div class="selector-label">We specialize in supplying goods for:</div>
                
                <Frame class="selector-controls" w="flex items-center gap-4">
                  <button 
                    @click="prevCategory"
                    class="arrow-btn"
                    aria-label="Previous category"
                  >
                    <i class="i-mdi:chevron-left" />
                  </button>
                  
                  <div class="current-category">
                    {{ categories[currentIndex].name }}
                  </div>
                  
                  <button 
                    @click="nextCategory"
                    class="arrow-btn"
                    aria-label="Next category"
                  >
                    <i class="i-mdi:chevron-right" />
                  </button>
                </Frame>
                
                <!-- Category Dots -->
                <Frame class="category-dots" w="flex justify-center gap-2 mt-4">
                  <button
                    v-for="(category, index) in categories"
                    :key="category.id"
                    @click="goToCategory(index)"
                    :class="['dot', { active: index === currentIndex }]"
                    :aria-label="`Go to ${category.name}`"
                  >
                  </button>
                </Frame>
              </Frame>
            </Frame>
            
          </Frame>
        </Frame>
      </Frame>


    </Frame>
    
   
    
  </div>
</template>

<style lang="scss" scoped>
.io-hero-content.hero-home {
  position: relative;
  width: 100%;

  /* Layer system */
  .layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &.bg-image-carousel {
      z-index: 1;
    }

    &.content {
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }

  /* Splide background carousel */
  :deep(.splide-background) {
    width: 100%;
    height: 100%;

    .splide__track {
      height: 100%;
    }

    .splide__list {
      height: 100%;
    }

    .slide-item {
      height: 100%;
      overflow: hidden;
    }

    .slide-bg-image {
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      transform-origin: center center;
      transform: scale(1);
      transition: none;

      &.ken-burns-active {
        animation: kenBurns 6s ease-out forwards;
      }
    }
  }

  /* Ken Burns zoom effect */
  @keyframes kenBurns {
    0% {
      transform: scale(1);
    }
    100% {
      transform: scale(1.1);
    }
  }

  /* Global carousel controls */
  .carousel-controls {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    z-index: 10;
    pointer-events: none;
    padding: 0 gr(2);

    .carousel-btn {
      position: absolute;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      cursor: pointer;
      pointer-events: all;

      i {
        font-size: 28px;
      }

      &:hover {
        background: rgba(0, 0, 0, 0.7);
        transform: scale(1.1);
        border-color: rgba(255, 255, 255, 0.4);
      }

      &.carousel-btn-prev {
        left: gr(2);
      }

      &.carousel-btn-next {
        right: gr(2);
      }
    }
  }

  .hero-content-wrapper {
    width: 100%;
    height: 100%;
  }

  /* Hairline text */
  .sec-hairline {
    font-size: fs(0);
    color: var(--cyan-400, #22d3ee);
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-weight: 500;
    text-align: left;

    @include mq($from: t) {
      font-size: fs(-1);
    }
  }

  /* Hero title */
  .hero-title {
    font-size: fs(6);
    font-weight: 400;
    line-height: 1.2;
    color: white;
    
    text-align: left;

    @include mq($until: t) {
      max-width: 80%;
      br {
        display:none;
      }
    }
    
    @include mq($from: ds) {
      font-size: fs(6);
    }
  }

  /* Hero description */
  .hero-description {
    font-size: fs(0);
    color: rgba(255, 255, 255, 0.9);
    line-height: gr(1.5);
    max-width: 90%;
  }

  /* Action buttons */
  .hero-actions {
    :deep(.hero-btn-primary) {
      background: var(--cyan-500, #06b6d4);
      color: white;
      padding: gr(0.75) gr(2);
      border-radius: 6px;
      font-weight: 600;
      transition: all 0.3s ease;

      &:hover {
        background: var(--cyan-600, #0891b2);
        transform: translateY(-2px);
      }
    }

    :deep(.hero-btn-outline) {
      background: transparent;
      color: white;
      border: 2px solid white;
      padding: gr(0.75) gr(2);
      border-radius: 6px;
      font-weight: 600;
      transition: all 0.3s ease;

      &:hover {
        background: white;
        color: var(--cyan-600, #0891b2);
      }
    }
  }

  /* Category selector */
  .category-selector {
    .selector-label {
      font-size: fs(-1);
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: gr(0.5);
      text-align: center;
    }

    .selector-controls {
      .arrow-btn {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.3);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        cursor: pointer;

        i {
          font-size: 24px;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }
      }

      .current-category {
        font-size: fs(1);
        font-weight: 600;
        color: white;
        min-width: 150px;
        text-align: center;
      }
    }

    .category-dots {
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.4);
        border: none;
        cursor: pointer;
        transition: all 0.3s ease;

        &.active {
          background: white;
          width: 24px;
          border-radius: 4px;
        }

        &:hover:not(.active) {
          background: rgba(255, 255, 255, 0.6);
        }
      }
    }
  }

  /* Responsive adjustments */
  @include mq($until: t) {
    .hero-content {
      text-align: center;
    }

    .hero-description {
      max-width: 100%;
    }

    .hero-actions {
      justify-content: center;
    }

    .content-right {
      margin-top: gr(3);
    }
  }
}
</style>